import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { authApi, type User } from "@/lib/authApi";
import { oauthApi } from "@/lib/oauthApi";
import { queryKeys } from "./useApi";

// Define user roles and types
type UserRole = "CREATOR" | "USER" | "ADMIN" | null;

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  hasCampaigns?: boolean;
}

interface AuthContextType {
  isLoading: boolean;
  user: AuthUser | null;
  handleGoogleLogin: () => void;
  handleLogin: (email: string, password: string) => void;
  handleRegister: (name: string, email: string, password: string) => void;
  handleLogout: () => void;
  isAuthenticated: boolean;
}

// Mock user data
const MOCK_USERS = {
  creator: {
    id: "user-123",
    name: "Alex Chen",
    email: "creator@example.com",
    role: "creator" as UserRole,
    hasCampaigns: true,
  },
  user: {
    id: "user-456",
    name: "Sarah Johnson",
    email: "user@example.com",
    role: "user" as UserRole,
    hasCampaigns: false,
  },
  admin: {
    id: "admin-789",
    name: "Admin User",
    email: "admin@example.com",
    role: "admin" as UserRole,
    hasCampaigns: true,
  },
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Get user from React Query cache or API
  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: queryKeys.user,
    queryFn: authApi.getProfile,
    enabled: authApi.isAuthenticated(),
    retry: false,
  });

  // Log out functionality
  const handleLogout = useCallback(() => {
    authApi.logout();
    toast({
      title: "Logged out successfully",
      description: "You have been logged out of your account.",
    });
    navigate("/login");
  }, [navigate]);

  // Real Google OAuth login
  const handleGoogleLogin = useCallback(async () => {
    setIsLoading(true);

    try {
      // Get OAuth URL from backend
      const { url } = await oauthApi.getGoogleAuthUrl();

      // Redirect to Google OAuth
      window.location.href = url;
    } catch (error) {
      toast({
        title: "OAuth failed",
        description:
          error instanceof Error ? error.message : "Failed to start OAuth flow",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  }, []);

  // Real API login
  const handleLogin = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);

      try {
        const response = await authApi.login({ email, password });
        toast({
          title: "Welcome back! 👋",
          description: `You've successfully logged in as ${response.user.role.toLowerCase()}.`,
        });
        navigate("/");
      } catch (error) {
        toast({
          title: "Login failed",
          description:
            error instanceof Error ? error.message : "Invalid credentials",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // Real API register
  const handleRegister = useCallback(
    async (name: string, email: string, password: string) => {
      setIsLoading(true);

      try {
        await authApi.register({ name, email, password });
        toast({
          title: "Registration successful! 🎉",
          description: "Welcome to FundORFling!",
        });
        navigate("/login");
      } catch (error) {
        toast({
          title: "Registration failed",
          description:
            error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // Convert API user to AuthUser format
  const authUser: AuthUser | null = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role as UserRole,
        hasCampaigns: false, // This will be determined by checking user's campaigns
      }
    : null;

  return (
    <AuthContext.Provider
      value={{
        isLoading: isLoading || isUserLoading,
        user: authUser,
        handleGoogleLogin,
        handleLogin,
        handleRegister,
        handleLogout,
        isAuthenticated: !!authUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
